import { useState } from "react";
import { Ruler } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Each chart: [size, measureA cm, measureA in, measureB cm, measureB in]
const CHARTS = {
  tops: {
    title: "Size guide — shirts & hoodies",
    colA: "Chest",
    colB: "Length",
    measure: "chest — around the fullest part; length — from shoulder seam to hem.",
    fit: "our performance shirts run slightly slim. Between sizes, or want a relaxed fit over a layer? Go one size up.",
    rows: [
      ["XXS", "86–91", "34–36", "64", "25"],
      ["XS", "91–96", "36–38", "68", "27"],
      ["S", "96–101", "38–40", "71", "28"],
      ["M", "101–106", "40–42", "74", "29"],
      ["L", "106–112", "42–44", "76", "30"],
      ["XL", "112–117", "44–46", "79", "31"],
      ["XXL", "117–122", "46–48", "81", "32"],
      ["3XL", "122–127", "48–50", "84", "33"],
      ["4XL", "127–132", "50–52", "86", "34"],
      ["5XL", "132–137", "52–54", "89", "35"],
      ["6XL", "137–142", "54–56", "91", "36"],
      ["7XL", "142–147", "56–58", "94", "37"],
    ],
  },
  shorts: {
    title: "Size guide — shorts",
    colA: "Waist",
    colB: "Hip",
    measure: "waist — around your natural waistline; hip — around the fullest part of your hips.",
    fit: "shorts have an elastic or part-elastic waist with a relaxed fit. Between sizes? Size down for a snug waist, up for extra room.",
    rows: [
      ["XXS", "64–69", "25–27", "84–89", "33–35"],
      ["XS", "69–74", "27–29", "89–94", "35–37"],
      ["S", "74–79", "29–31", "94–99", "37–39"],
      ["M", "79–84", "31–33", "99–104", "39–41"],
      ["L", "84–90", "33–35", "104–110", "41–43"],
      ["XL", "90–95", "35–37", "110–115", "43–45"],
      ["XXL", "95–100", "37–39", "115–120", "45–47"],
      ["3XL", "100–106", "39–42", "120–126", "47–50"],
      ["4XL", "106–112", "42–44", "126–132", "50–52"],
      ["5XL", "112–118", "44–46", "132–138", "52–54"],
      ["6XL", "118–124", "46–49", "138–144", "54–57"],
      ["7XL", "124–130", "49–51", "144–150", "57–59"],
    ],
  },
  pants: {
    title: "Size guide — long pants",
    colA: "Waist",
    colB: "Inseam",
    measure: "waist — around your natural waistline; inseam — from the crotch seam to the bottom of the leg.",
    fit: "pants run true to size with a straight leg. Between sizes? Go up for a relaxed fit — the waist is adjustable on most styles.",
    rows: [
      ["XXS", "64–69", "25–27", "74", "29"],
      ["XS", "69–74", "27–29", "76", "30"],
      ["S", "74–79", "29–31", "78", "30.5"],
      ["M", "79–84", "31–33", "80", "31.5"],
      ["L", "84–90", "33–35", "82", "32"],
      ["XL", "90–95", "35–37", "83", "32.5"],
      ["XXL", "95–100", "37–39", "84", "33"],
      ["3XL", "100–106", "39–42", "85", "33.5"],
      ["4XL", "106–112", "42–44", "86", "34"],
      ["5XL", "112–118", "44–46", "87", "34"],
      ["6XL", "118–124", "46–49", "88", "34.5"],
      ["7XL", "124–130", "49–51", "89", "35"],
    ],
  },
};

export const SIZE_CHART_OPTIONS = [
  { value: "tops", label: "Shirts & hoodies (chest/length)" },
  { value: "shorts", label: "Shorts (waist/hip)" },
  { value: "pants", label: "Long pants (waist/inseam)" },
];

export const SizeGuide = ({ sizes = [], chart = "tops" }) => {
  const [open, setOpen] = useState(false);
  const c = CHARTS[chart] || CHARTS.tops;
  const rows = sizes.length ? c.rows.filter(([s]) => sizes.includes(s)) : c.rows;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-sm font-bold underline underline-offset-4 hover:text-brand"
        data-testid="size-guide-btn"
      >
        <Ruler size={15} /> Size guide
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" aria-describedby={undefined} data-testid="size-guide-dialog">
          <DialogHeader><DialogTitle>{c.title}</DialogTitle></DialogHeader>
          <div className="text-sm space-y-4">
            <table className="w-full border border-ink/10" data-testid="size-guide-table">
              <thead className="bg-secondary text-xs uppercase tracking-wide">
                <tr>
                  <th rowSpan={2} className="p-2.5 text-left border-r border-ink/10">Size</th>
                  <th colSpan={2} className="p-2 text-center border-r border-ink/10 border-b border-ink/10">{c.colA}</th>
                  <th colSpan={2} className="p-2 text-center border-b border-ink/10">{c.colB}</th>
                </tr>
                <tr>
                  <th className="p-1.5 text-center font-medium">cm</th>
                  <th className="p-1.5 text-center font-medium border-r border-ink/10">inches</th>
                  <th className="p-1.5 text-center font-medium">cm</th>
                  <th className="p-1.5 text-center font-medium">inches</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([s, acm, ain, bcm, bin]) => (
                  <tr key={s} className="border-t border-ink/5">
                    <td className="p-2.5 font-bold border-r border-ink/10">{s}</td>
                    <td className="p-2.5 text-center">{acm}</td>
                    <td className="p-2.5 text-center text-muted-foreground border-r border-ink/10">{ain}"</td>
                    <td className="p-2.5 text-center">{bcm}</td>
                    <td className="p-2.5 text-center text-muted-foreground">{bin}"</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="bg-secondary p-3 text-xs text-muted-foreground space-y-1.5 rounded-md">
              <p><strong className="text-ink">How to measure:</strong> {c.measure}</p>
              <p><strong className="text-ink">Fit tip:</strong> {c.fit}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
